/*
 * Kind Curations — Size availability
 * -----------------------------------------------------------------------------
 * 1. Collection / search / featured grids: shows the size run on each product
 *    card, with sold-out sizes struck through. In-stock sizes add straight to
 *    the cart (single-option products) or deep-link to the pre-selected variant.
 * 2. Product pages: sold-out sizes are struck through and cannot be selected.
 *
 * Self-contained. To remove entirely: delete this file, delete
 * kc-size-availability.css, and remove the two tagged lines in layout/theme.liquid.
 * -----------------------------------------------------------------------------
 */
(function () {
  'use strict';

  var CONFIG = {
    // Click an in-stock size on a product card to add it to the cart.
    // Set to false to make the sizes plain links to the product page instead.
    quickAdd: true,

    // Product page: true  = sold-out sizes are struck through AND unclickable.
    //               false = struck through but still selectable, which keeps the
    //                       Klaviyo "Notify me when available" flow reachable.
    disableSoldOutOnProductPage: true,

    // Hide Boost's duplicate mini-cart so only the theme's cart drawer shows.
    // Set to false if Boost's Ajax cart is ever switched off in the Boost app,
    // or if you decide you want Boost's mini-cart to be the cart.
    hideBoostMiniCart: true,

    // Don't show a size row for products with fewer than this many sizes.
    minSizes: 2,

    // Cap the number of pills on a card so the grid stays tidy.
    maxPills: 10,

    // Option names treated as "size". Anything containing "size" also matches.
    sizeOptionNames: [
      'size', 'sizes', 'taille', 'größe', 'grosse', 'tamanho', 'tamaño',
      'shoe size', 'uk size', 'us size', 'au size', 'eu size', 'band size'
    ]
  };

  // ---------------------------------------------------------------- utilities
  function each(list, fn) {
    if (!list) return;
    Array.prototype.slice.call(list).forEach(fn);
  }

  function isSizeName(name) {
    var n = String(name || '').trim().toLowerCase();
    if (!n) return false;
    if (CONFIG.sizeOptionNames.indexOf(n) > -1) return true;
    return n.indexOf('size') > -1;
  }

  function handleFromUrl(url) {
    var m = String(url || '').match(/\/products\/([^/?#]+)/);
    return m ? m[1] : null;
  }

  // ------------------------------------------------------------- size dataset
  // handle -> null (no size option) | { label, multiOption, values: [...] }
  var CACHE = {};
  var WAITING = {};

  function buildEntry(optionNames, values, variantMatcher) {
    var entry = {
      multiOption: optionNames.length > 1,
      values: []
    };
    values.forEach(function (value) {
      var match = variantMatcher(value);
      entry.values.push({
        value: value,
        available: !!(match && match.available),
        id: match ? match.id : null
      });
    });
    return entry;
  }

  // Boost AI Search & Discovery payload
  function storeBoostProduct(p) {
    if (!p || !p.handle || Object.prototype.hasOwnProperty.call(CACHE, p.handle)) return;

    var options = p.options_with_values || [];
    var sizeOption = null;
    for (var i = 0; i < options.length; i++) {
      var name = options[i].name || options[i].original_name || '';
      if (isSizeName(name)) { sizeOption = options[i]; break; }
    }
    if (!sizeOption) { CACHE[p.handle] = null; return; }

    var key = String(sizeOption.name || sizeOption.original_name || '').toLowerCase();
    var order = (sizeOption.values || []).map(function (v) {
      return typeof v === 'string' ? v : v.title;
    });
    var onlyOption = options.length === 1;

    var byValue = {};
    (p.variants || []).forEach(function (v) {
      var value = null;
      (v.merged_options || []).forEach(function (mo) {
        var sep = String(mo).indexOf(':');
        if (sep > -1 && String(mo).slice(0, sep).toLowerCase() === key) {
          value = String(mo).slice(sep + 1);
        }
      });
      if (value === null && onlyOption) value = v.title;
      if (value === null) return;
      // Prefer an available variant when several share the same size.
      if (!byValue[value] || (!byValue[value].available && v.available)) {
        byValue[value] = { available: !!v.available, id: v.id };
      }
    });

    CACHE[p.handle] = buildEntry(options, order, function (value) {
      return byValue[value] || null;
    });
  }

  function harvestBoost(payload) {
    try {
      var products = payload && (payload.products ||
        (payload.results && payload.results.products));
      if (!products || !products.length) return;
      products.forEach(storeBoostProduct);
      flushWaiting();
    } catch (e) { /* never break the page over this */ }
  }

  // Shopify /products/<handle>.js payload (fallback)
  function storeShopifyProduct(handle, p) {
    if (!p || !p.options) { CACHE[handle] = null; return flushWaiting(); }

    var names = p.options.map(function (o) {
      return typeof o === 'string' ? o : o.name;
    });
    var position = -1;
    for (var i = 0; i < names.length; i++) {
      if (isSizeName(names[i])) { position = i; break; }
    }
    if (position === -1 && names.length === 1) position = 0;
    if (position === -1) { CACHE[handle] = null; return flushWaiting(); }

    var order = [];
    (p.variants || []).forEach(function (v) {
      var value = v.options ? v.options[position] : v['option' + (position + 1)];
      if (value != null && order.indexOf(value) === -1) order.push(value);
    });

    var byValue = {};
    (p.variants || []).forEach(function (v) {
      var value = v.options ? v.options[position] : v['option' + (position + 1)];
      if (value == null) return;
      if (!byValue[value] || (!byValue[value].available && v.available)) {
        byValue[value] = { available: !!v.available, id: v.id };
      }
    });

    CACHE[handle] = buildEntry(names, order, function (value) {
      return byValue[value] || null;
    });
    flushWaiting();
  }

  // -------------------------------------------- read Boost's own result state
  // Boost fetches its results over JSONP, so there is no request to intercept.
  // It does keep the current page of products on its instance, which already
  // carries every variant's size and stock status — no extra requests needed.
  var seenSets = (typeof WeakSet === 'function') ? new WeakSet() : null;

  function boostState() {
    var found = [];
    try {
      var root = window.boostPFS;
      if (!root) return found;
      ['filter', 'search', 'instantSearch'].forEach(function (key) {
        var node = root[key];
        if (!node) return;
        [node, node.filterResult, node.searchResult].forEach(function (candidate) {
          if (candidate && candidate.data && candidate.data.products &&
              candidate.data.products.length) {
            found.push(candidate.data.products);
          }
        });
      });
    } catch (e) {}
    return found;
  }

  function harvestBoostState() {
    var sets = boostState();
    for (var i = 0; i < sets.length; i++) {
      if (seenSets) {
        if (seenSets.has(sets[i])) continue;
        seenSets.add(sets[i]);
      }
      harvestBoost({ products: sets[i] });
    }
  }

  // -------------------------------------------------------- fallback fetching
  var queue = [];
  var inFlight = 0;
  var MAX_PARALLEL = 5;

  function requestHandle(handle) {
    if (Object.prototype.hasOwnProperty.call(CACHE, handle)) return;
    if (WAITING[handle] === 'requested') return;
    WAITING[handle] = 'requested';
    queue.push(handle);
    pump();
  }

  function pump() {
    while (inFlight < MAX_PARALLEL && queue.length) {
      (function (handle) {
        inFlight++;
        var url = (window.routes && window.routes.rootUrl ? window.routes.rootUrl.replace(/\/$/, '') : '') +
          '/products/' + handle + '.js';
        fetch(url, { credentials: 'same-origin' })
          .then(function (r) { return r.ok ? r.json() : null; })
          .then(function (data) { storeShopifyProduct(handle, data); })
          .catch(function () { CACHE[handle] = null; flushWaiting(); })
          .then(function () { inFlight--; pump(); });
      })(queue.shift());
    }
  }

  // --------------------------------------------------------------- card sizes
  var pendingCards = [];

  function flushWaiting() {
    var still = [];
    pendingCards.forEach(function (item) {
      if (Object.prototype.hasOwnProperty.call(CACHE, item.handle)) {
        paintCard(item.card, item.handle);
      } else {
        still.push(item);
      }
    });
    pendingCards = still;
  }

  function collectCards(root) {
    var scope = root && root.querySelectorAll ? root : document;
    var cards = scope.querySelectorAll('.ProductItem, .boost-pfs-filter-product-item');
    each(cards, considerCard);
  }

  // Cards Boost hasn't told us about are only looked up once they scroll near
  // the viewport, so a grid never fires a burst of requests up front.
  var lazyObserver = null;
  if (window.IntersectionObserver) {
    lazyObserver = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        lazyObserver.unobserve(entry.target);
        scheduleSweep();
      });
    }, { rootMargin: '300px 0px' });
  }

  // IntersectionObserver callbacks are throttled while a tab is in the
  // background, so a rect-based sweep backs it up.
  var sweepTimer = null;

  // Boost paints its cards a beat before its result data is readable, so give
  // it a grace period before falling back to a per-product request.
  var GRACE_MS = 2000;

  function sweep() {
    if (!pendingCards.length) return;
    harvestBoostState();
    var limit = (window.innerHeight || 800) + 400;
    var now = Date.now();
    pendingCards.forEach(function (item) {
      if (Object.prototype.hasOwnProperty.call(CACHE, item.handle)) return;
      if (WAITING[item.handle] === 'requested') return;
      if (now - item.t < GRACE_MS) return;
      var rect = item.card.getBoundingClientRect();
      if (rect.bottom > -400 && rect.top < limit) requestHandle(item.handle);
    });
  }

  function scheduleSweep() {
    if (sweepTimer) return;
    sweepTimer = setTimeout(function () { sweepTimer = null; sweep(); }, 250);
  }

  var graceTimer = null;
  function scheduleGraceSweep() {
    if (graceTimer) return;
    graceTimer = setTimeout(function () { graceTimer = null; sweep(); }, GRACE_MS + 250);
  }

  window.addEventListener('scroll', scheduleSweep, { passive: true });
  window.addEventListener('resize', scheduleSweep);

  function considerCard(card) {
    if (!card || card.getAttribute('data-kc-sizes')) return;
    if (card.closest && card.closest('.Drawer, .Cart, .CartItem')) return;

    var link = card.querySelector('a[href*="/products/"]');
    if (!link) return;
    var handle = handleFromUrl(link.getAttribute('href'));
    if (!handle) return;

    card.setAttribute('data-kc-sizes', 'pending');
    card.setAttribute('data-kc-handle', handle);

    harvestBoostState();

    if (Object.prototype.hasOwnProperty.call(CACHE, handle)) {
      paintCard(card, handle);
      return;
    }

    pendingCards.push({ card: card, handle: handle, t: Date.now() });
    if (lazyObserver) lazyObserver.observe(card);
    scheduleGraceSweep();
  }

  function paintCard(card, handle) {
    card.setAttribute('data-kc-sizes', 'done');
    var entry = CACHE[handle];
    if (!entry || !entry.values || entry.values.length < CONFIG.minSizes) return;
    if (!document.body.contains(card)) return;
    if (card.querySelector('.kc-Sizes')) return;

    var values = entry.values.slice(0, CONFIG.maxPills);
    var list = document.createElement('ul');
    list.className = 'kc-Sizes';
    list.setAttribute('aria-label', 'Sizes');

    values.forEach(function (item) {
      var li = document.createElement('li');
      var node;

      if (!item.available) {
        node = document.createElement('span');
        node.className = 'kc-Size kc-Size--out';
        node.setAttribute('aria-label', item.value + ' — sold out');
      } else if (CONFIG.quickAdd && !entry.multiOption && item.id) {
        node = document.createElement('button');
        node.type = 'button';
        node.className = 'kc-Size';
        node.setAttribute('data-kc-variant', item.id);
        node.setAttribute('aria-label', 'Add size ' + item.value + ' to cart');
      } else {
        node = document.createElement('a');
        node.className = 'kc-Size';
        node.href = '/products/' + handle + (item.id ? '?variant=' + item.id : '');
        node.setAttribute('aria-label', 'View size ' + item.value);
      }

      node.textContent = item.value;
      li.appendChild(node);
      list.appendChild(li);
    });

    if (entry.values.length > CONFIG.maxPills) {
      var more = document.createElement('li');
      var moreLink = document.createElement('a');
      moreLink.className = 'kc-Size kc-Size--more';
      moreLink.href = '/products/' + handle;
      moreLink.textContent = '+' + (entry.values.length - CONFIG.maxPills);
      more.appendChild(moreLink);
      list.appendChild(more);
    }

    var host = card.querySelector('.ProductItem__Info') ||
      card.querySelector('.boost-pfs-filter-product-bottom') ||
      card.querySelector('.ProductItem__Wrapper') ||
      card;
    host.appendChild(list);
  }

  // ------------------------------------------------------------ quick add
  function addToCart(button) {
    var id = button.getAttribute('data-kc-variant');
    if (!id || button.classList.contains('is-busy')) return;
    button.classList.add('is-busy');

    var addUrl = (window.routes && window.routes.cartAddUrl) || '/cart/add.js';

    fetch(addUrl, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ id: Number(id), quantity: 1 })
    })
      .then(function (r) { return r.json().then(function (d) { return { ok: r.ok, data: d }; }); })
      .then(function (result) {
        button.classList.remove('is-busy');
        if (!result.ok) {
          // Out of stock between page load and click — reflect it and stop.
          button.outerHTML = '<span class="kc-Size kc-Size--out">' + button.textContent + '</span>';
          return;
        }
        button.classList.add('is-added');
        setTimeout(function () { button.classList.remove('is-added'); }, 1600);
        openCart(result.data);
      })
      .catch(function () {
        button.classList.remove('is-busy');
        window.location.href = button.closest('.ProductItem, .boost-pfs-filter-product-item')
          .querySelector('a[href*="/products/"]').href;
      });
  }

  function openCart(item) {
    try {
      document.documentElement.dispatchEvent(new CustomEvent('product:added', {
        bubbles: true, detail: { variant: item, quantity: 1 }
      }));
      document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
    } catch (e) {}

    setTimeout(function () {
      var drawer = document.getElementById('sidebar-cart');
      if (drawer && drawer.getAttribute('aria-hidden') === 'false') return;
      var toggle = document.querySelector('[data-action="open-drawer"][data-drawer-id="sidebar-cart"]');
      if (toggle) toggle.click();
    }, 350);
  }

  document.addEventListener('click', function (event) {
    var button = event.target.closest && event.target.closest('button.kc-Size');
    if (!button) return;
    event.preventDefault();
    event.stopPropagation();
    addToCart(button);
  });

  // --------------------------------------------------------- product page
  function initProductForms() {
    each(document.querySelectorAll('script[data-product-json]'), function (script) {
      if (script.getAttribute('data-kc-bound')) return;
      script.setAttribute('data-kc-bound', '1');

      var payload;
      try { payload = JSON.parse(script.textContent); } catch (e) { return; }
      var product = payload && payload.product;
      if (!product || !product.variants || product.variants.length < 2) return;

      var names = (product.options || []).map(function (o) {
        return typeof o === 'string' ? o : o.name;
      });

      var positions = [];
      names.forEach(function (name, i) { if (isSizeName(name)) positions.push(i + 1); });
      if (!positions.length && names.length === 1) positions = [1];
      if (!positions.length) return;

      var scope = (script.closest && script.closest('.shopify-section')) || document;
      var popoverPrefix = 'popover-' + product.id + '-';

      function selection() {
        var sel = {};
        each(scope.querySelectorAll('input[type="radio"][data-option-position]'), function (radio) {
          if (radio.checked) sel[radio.getAttribute('data-option-position')] = radio.value;
        });
        each(document.querySelectorAll('[id^="' + popoverPrefix + '"] .Popover__Value.is-selected'), function (btn) {
          var pos = btn.getAttribute('data-option-position');
          if (pos && !(pos in sel)) sel[pos] = btn.getAttribute('data-value');
        });
        return sel;
      }

      function available(position, value, sel) {
        return product.variants.some(function (variant) {
          if (variant['option' + position] !== value) return false;
          for (var p = 1; p <= names.length; p++) {
            if (p === position) continue;
            if (sel[p] != null && variant['option' + p] !== sel[p]) return false;
          }
          return variant.available;
        });
      }

      function apply() {
        var sel = selection();
        positions.forEach(function (position) {
          each(scope.querySelectorAll('.SizeSwatch__Radio[data-option-position="' + position + '"]'), function (radio) {
            var ok = available(position, radio.value, sel);
            var label = scope.querySelector('label[for="' + radio.id + '"]');
            if (CONFIG.disableSoldOutOnProductPage) radio.disabled = !ok && !radio.checked;
            if (label) label.classList[ok ? 'remove' : 'add']('kc-is-unavailable');
          });

          each(document.querySelectorAll('[id^="' + popoverPrefix + '"] .Popover__Value[data-option-position="' + position + '"]'), function (btn) {
            var ok = available(position, btn.getAttribute('data-value'), sel);
            btn.classList[ok ? 'remove' : 'add']('kc-is-unavailable');
            if (CONFIG.disableSoldOutOnProductPage) {
              btn.disabled = !ok && !btn.classList.contains('is-selected');
            }
          });
        });
      }

      apply();
      document.addEventListener('change', function () { setTimeout(apply, 0); });
      document.addEventListener('variant:changed', function () { setTimeout(apply, 0); });
      document.addEventListener('click', function () { setTimeout(apply, 120); });
    });
  }

  // ------------------------------------------------------- Boost mini-cart
  // The CSS hides Boost's mini-cart (see the note in kc-size-availability.css).
  // Boost still thinks it opened it, and locks page scrolling when it does —
  // so clear that lock, or the shopper is left on a page that won't scroll.
  // The theme's own 'no-scroll' class is left alone; that one is legitimate.
  var BOOST_LOCK = 'boost-pfs-body-no-scroll-mini-cart';

  function clearBoostScrollLock() {
    [document.documentElement, document.body].forEach(function (el) {
      if (el && el.classList.contains(BOOST_LOCK)) el.classList.remove(BOOST_LOCK);
    });
  }

  function watchBoostScrollLock() {
    if (!CONFIG.hideBoostMiniCart) return;
    clearBoostScrollLock();
    if (!window.MutationObserver) return;
    [document.documentElement, document.body].forEach(function (el) {
      if (!el) return;
      new MutationObserver(clearBoostScrollLock)
        .observe(el, { attributes: true, attributeFilter: ['class'] });
    });
  }

  // ------------------------------------------------------------------- boot
  function start() {
    watchBoostScrollLock();
    harvestBoostState();
    collectCards(document);
    initProductForms();

    // Boost renders asynchronously; re-check its state for a short while.
    [200, 600, 1200, 2500, 4000].forEach(function (delay) {
      setTimeout(function () {
        harvestBoostState();
        collectCards(document);
        sweep();
      }, delay);
    });

    if (window.MutationObserver) {
      new MutationObserver(function (mutations) {
        var touched = false;
        harvestBoostState();
        mutations.forEach(function (m) {
          each(m.addedNodes, function (node) {
            if (node.nodeType !== 1) return;
            touched = true;
            if (node.matches && node.matches('.ProductItem, .boost-pfs-filter-product-item')) {
              considerCard(node);
            }
            collectCards(node);
          });
        });
        if (touched) initProductForms();
      }).observe(document.body, { childList: true, subtree: true });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
